/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState } from 'react';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDSnackbar from 'components/MDSnackbar';
import FormQuantity from 'components/Form/FormQuantity';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

function ExportRequestStock({ parent, row, view, openPhoto }) {
  const mod = 'export_request_list';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const edit = (item, kind) => {
    setLoading(true);

    FetchData('PATCH', `/${mod}/${item.id}`, app.token, {
      kind: kind,
      price: item.price,
      quantity: item.quantity,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const del = (item) => {
    setLoading(true);

    FetchData('DELETE', `/${mod}/${item.id}`, app.token, {}).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.deletecomplete`), 'success');
      } else {
        showNotify(t(`alert.deleteerror`), 'error');
      }
    });
  };

  const btnDel = (item) => {
    if (parent.status === 'cancel') return null;
    if (item.quantity_use * 1 > 0) return null;

    return (
      <MDButton
        variant='text'
        tabIndex={-1}
        color='error'
        size='small'
        iconOnly
        onClick={() => del(item)}
      >
        <Icon>delete</Icon>
      </MDButton>
    );
  };

  const pharmacyDisplay = () => {
    if (row.pharmacy === 'Y') {
      if (row.medicine === 'Y') {
        return (
          <span>
            <i className='fas fa-prescription color-success' /> ยาสามัญ
          </span>
        );
      }

      return (
        <span>
          <i className='fas fa-prescription color-danger' /> เฉพาะร้านยา
        </span>
      );
    }

    return null;
  };

  const renderList = (stock) => {
    return (
      <Collapse in={open} timeout='auto' unmountOnExit>
        <MDBox p={1}>
          <TableContainer sx={{ boxShadow: 0 }}>
            <Table aria-label='simple table'>
              <TableHead>
                <TableRow>
                  <TableCell align='center' width={10}>
                    No.
                  </TableCell>
                  <TableCell align='left'>คลัง</TableCell>
                  <TableCell align='left'>ตำแหน่งวาง</TableCell>
                  <TableCell align='left' width={120}>
                    Lot
                  </TableCell>
                  <TableCell align='left' width={120}>
                    วันหมดอายุ
                  </TableCell>
                  <TableCell align='right' width={120}>
                    จำนวน
                  </TableCell>
                  <TableCell align='right' width={120}>
                    หยิบแล้ว
                  </TableCell>
                  <TableCell align='right' width={120}>
                    ตรวจแล้ว
                  </TableCell>
                  <TableCell align='left' width={120}>
                    หน่วย
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stock.map((item, i) => (
                  <TableRow key={item.id}>
                    <TableCell align='center'>{i + 1}</TableCell>
                    <TableCell align='left'>{item.warehouse_name}</TableCell>
                    <TableCell align='left'>{item.location_code}</TableCell>
                    <TableCell align='left'>{item.lot}</TableCell>
                    <TableCell align='left'>
                      {item.is_expire === 'Y' ? Func.dateDisplay(item.date_expire) : null}
                    </TableCell>
                    <TableCell align='right'>{Func.numberDisplay(item.quantity)}</TableCell>
                    <TableCell align='right'>{Func.numberDisplay(item.quantity_get)}</TableCell>
                    <TableCell align='right'>{Func.numberDisplay(item.quantity_stock)}</TableCell>
                    <TableCell align='left'>{item.unit_name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </MDBox>
      </Collapse>
    );
  };

  return (
    <TableBody>
      <TableRow>
        <TableCell align='center'>
          <IconButton aria-label='expand row' size='small' onClick={() => setOpen(!open)}>
            {open ? <Icon>keyboard_arrow_up</Icon> : <Icon>keyboard_arrow_down</Icon>}
          </IconButton>
        </TableCell>
        <TableCell align='center'>{row.no}</TableCell>
        <TableCell onClick={() => openPhoto(row)}>{Func.imageDisplay(row)}</TableCell>
        <TableCell>
          <MDBox className='nowrap'>
            {row.code}{' '}
            {row.code === row.sku ? '' : <span className='color-default'>{row.sku}</span>}
          </MDBox>
          <MDBox className='cut' width={400}>
            {row.name}
          </MDBox>
        </TableCell>
        <TableCell align='center'>{pharmacyDisplay()}</TableCell>
        <TableCell align='right' className='color-primary'>
          {parent.status === 'edit' ? (
            <FormQuantity parent={row} edit={edit} />
          ) : (
            Func.numberDisplay(row.quantity)
          )}
        </TableCell>
        <TableCell align='right' className='color-danger'>
          {Func.numberDisplay(row.quantity_use)}
        </TableCell>
        <TableCell align='right' className='color-info'>
          {Func.numberDisplay(row.quantity_stock)}
        </TableCell>
        <TableCell align='right' className='color-success'>
          {Func.numberDisplay(row.quantity_receive)}
        </TableCell>
        <TableCell align='left'>{row.unit_name}</TableCell>
        <TableCell align='center'>{btnDel(row)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={11} sx={{ backgroundColor: '#fafafa', padding: 0 }}>
          {renderList(row.stock)}
        </TableCell>
      </TableRow>
    </TableBody>
  );
}

ExportRequestStock.propTypes = {
  parent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
  row: PropTypes.shape({
    id: PropTypes.string.isRequired,
    no: PropTypes.number.isRequired,
    barcode: PropTypes.string.isRequired,
    size_name: PropTypes.string.isRequired,
    color_name: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
    sku: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    pharmacy: PropTypes.string.isRequired,
    medicine: PropTypes.string.isRequired,
    product_name: PropTypes.string.isRequired,
    quantity: PropTypes.string.isRequired,
    quantity_receive: PropTypes.string.isRequired,
    quantity_use: PropTypes.string.isRequired,
    quantity_balance: PropTypes.string.isRequired,
    quantity_stock: PropTypes.string.isRequired,
    unit_name: PropTypes.string.isRequired,
    problem_name: PropTypes.string.isRequired,
    invite_id: PropTypes.string.isRequired,
    stock: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        quantity: PropTypes.string.isRequired,
        quantity_stock: PropTypes.string.isRequired,
        is_expire: PropTypes.string.isRequired,
        date_expire: PropTypes.string.isRequired,
        unit_name: PropTypes.string.isRequired,
        lot: PropTypes.string.isRequired,
        warehouse_name: PropTypes.string.isRequired,
        location_code: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
  }).isRequired,
  view: PropTypes.func.isRequired,
  openPhoto: PropTypes.func.isRequired,
};

export default ExportRequestStock;
